import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Page } from "../compositions/Page";
import { selectedScenariosAtom } from "../shared/state/atoms";
import {
  getResidenceTypeList,
  ResidenceTypeModel,
} from "../clients/KompasApi";
import { usePagination } from "../shared/hooks/usePagination";
import { useIsAuthenticated } from "@azure/msal-react";
import { currentSituationAtom } from "../compositions/modals/SituationModal";
import { ResidenceTypeImage } from "../components/ResidenceTypeImage";
import { ResidenceTypeFilters } from "../components/ResidenceTypeFilters";
import { customerIdAtom } from "../components/ResidenceTypeFilters/CustomerFilter";
import { residenceCategoryIdsAtom } from "../components/ResidenceTypeFilters/ResidenceCategoryFilter";
import { roofTypeIdsAtom } from "../components/ResidenceTypeFilters/RoofTypeFilter";

export function HomePage() {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [, setSelectedScenarios] = useAtom(selectedScenariosAtom);
  const [, setCurrentSituation] = useAtom(currentSituationAtom);

  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = useState(false);
  const { offset, limit, loadMore, setOffset } =
    usePagination("ResidenceTypes");
  const [residenceTypes, setResidenceTypes] = useState<ResidenceTypeModel[]>(
    []
  );
  const [totalResidenceTypeCount, setTotalResidenceTypeCount] = useState(0);

  const [customerId] = useAtom(customerIdAtom);
  const [roofTypeIds] = useAtom(roofTypeIdsAtom);
  const [residenceCategoryIds] = useAtom(residenceCategoryIdsAtom);

  const residenceTypeQuery = useMemo(
    () => ({
      CustomerId: customerId,
      RoofTypeIds: roofTypeIds,
      ResidenceCategoryIds: residenceCategoryIds,
      Search: search
    }),
    [customerId, roofTypeIds, residenceCategoryIds, search]
  );

  useEffect(() => {
    setOffset(0);
  }, [residenceTypeQuery, setOffset]);

  useEffect(() => {
    setSelectedScenarios([]);
    setCurrentSituation(undefined);
  }, [setSelectedScenarios, setCurrentSituation]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const abortController = new AbortController();

    setLoading(true);

    getResidenceTypeList({
      query: {
        Offset: offset,
        Limit: limit,
        ...residenceTypeQuery,
      },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) {
          setResidenceTypes((prev) => {
            prev.splice(offset, limit, ...(data.items ?? []));
            return prev.slice(0, offset + limit);
          });
          setTotalResidenceTypeCount(data.totalCount ?? 0);
        }
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        throw e;
      })
      .finally(() => setLoading(false));

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [offset, limit, residenceTypeQuery, isAuthenticated]);

  return (
    <Page flex>
      <Box mb={8}>
        <Box maxW="1400px" mx="auto">
          <Heading as="h1">Kies uw woningtype</Heading>
          <Text>
            Selecteer een woningtype om te beginnen met het vergelijken van
            mogelijke scenario&apos;s
          </Text>
        </Box>
        <Flex gap={4} maxW="1400px" mt={8} mx="auto">
          <ResidenceTypeFilters search={search} />
          <Box bg="white" borderRadius={8} flex={3} pt={4} px={4}>
            <Heading as="h2" mb={4} size="md">
              Selecteer woningtype
            </Heading>
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.600" />
              </InputLeftElement>
              <Input
                _placeholder={{
                  fontStyle: "italic"
                }}
                onChange={(e) => setSearch(e.target.value?.trim() ?? "")}
                placeholder="Zoek op naam, beschrijving of bouwjaar"
                value={search}
              />
              <InputRightElement>
                <CloseIcon
                  boxSize={2.5}
                  color="gray.600"
                  cursor="pointer"
                  onClick={() => setSearch("")}
                />
              </InputRightElement>
            </InputGroup>
            <Text size="xs">
              {totalResidenceTypeCount}{" "}
              {totalResidenceTypeCount === 1 ? "woningtype" : "woningtypes"}
            </Text>
            {residenceTypes.length > 0 ? (
              <Box maxH="calc(100vh - 475px)" overflowY="auto">
                {residenceTypes.map((residenceType) => (
                  <Flex
                    key={residenceType.id}
                    alignItems="center"
                    flexDir="row"
                    my={4}
                    pr={4}
                  >
                    <ResidenceTypeImage
                      fallback={residenceType.name}
                      id={residenceType.id}
                    />
                    <Box flex={1}>
                      <Text fontWeight="bold">
                        Woningtype {residenceType.name}
                      </Text>
                      <Text>
                        {[residenceType.category, residenceType.roofType].join(
                          " - "
                        )}
                      </Text>
                      <Text fontStyle="italic">
                        {residenceType.scenarioCount}{" "}
                        {residenceType.scenarioCount === 1
                          ? "berekening"
                          : "berekeningen"}
                      </Text>
                    </Box>
                    <Button
                      onClick={() => navigate(`/residence/${residenceType.id}`)}
                      variant="outline"
                    >
                      Bekijk woning
                      <Image
                        alt="Chevron"
                        filter="var(--filter-red-110)"
                        h={4}
                        ml={2}
                        src="/svg/chevron.svg"
                        transform="rotate(90deg)"
                        w={4}
                      />
                    </Button>
                  </Flex>
                ))}
                <Flex alignItems="center">
                  {residenceTypes.length < totalResidenceTypeCount && (
                    <Button disabled={loading} onClick={loadMore}>
                      Meer laden
                    </Button>
                  )}
                  {loading && <Spinner display="block" ml={4} />}
                </Flex>
              </Box>
            ) : (
              <Text>
                Er zijn geen woningen gevonden die voldoen aan de criteria.
              </Text>
            )}
          </Box>
        </Flex>
      </Box>
    </Page>
  );
}
